import React, {useMemo, useState} from 'react';
import Item from './itemTask/ItemTaskInfoViewSingleVoyage'
import {sx} from './sx'
import Stack from '@mui/material/Stack'
import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import {SxType} from "@baseTypes/sx/sxTypes";
import {ITaskSingleVoyage, SubTaskSingleVoyage} from "@containers/singleVoyage/tasks/types";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {taskStateSingleVoyage} from "@containers/singleVoyage/tasks/helpers/executions";
import TaskStateDotSingleVoyage
    from "@containers/singleVoyage/tasks/views/pointTasksInfo/itemTask/TaskStateDotSingleVoyage";
import {sx as itemSx} from "@containers/singleVoyage/tasks/views/pointTasksInfo/itemTask/sx";

interface IProps {
    tasks: ITaskSingleVoyage[],
    sx?: {
        card?: SxType
    },
    showDescription?: boolean,
    hasChangeStatuses?: boolean
    voyageId?: number
    accountId: string | number
    currentWaypointId?: number
    narrowToWaypoint?: boolean
}

const PointTasksInfoSmallViewSingleVoyage = ({
                                                 tasks,
                                                 sx: sxProps = {},
                                                 showDescription,
                                                 hasChangeStatuses,
                                                 voyageId,
                                                 accountId,
                                                 currentWaypointId,
                                                 narrowToWaypoint,
                                             }: IProps) => {

    const {t} = useTranslation()

    /**
     * Folded by default. A task that covers four stops was printed under every one
     * of them, so a three-stop voyage with four tasks put twelve rows on the left
     * before saying anything about any of them. The fold keeps the answer this
     * panel owes - how much work is here and how it stands - and puts the names a
     * click away.
     */
    const [open, setOpen] = useState(false)

    /**
     * The fold has to stand for what is behind it, subtasks included - a stop with
     * four tasks and three subtasks is seven pieces of work, and a summary that
     * counted four was answering a different question than the list it hid.
     * Subtasks get the small mark, so the shape of the row matches the shape of
     * the list it hides. Only work that is actually at this stop is counted,
     * because only that work is listed.
     */
    const states = useMemo(() => {
        const inPointContext = typeof currentWaypointId === 'number'
        const executionHere = (item: ITaskSingleVoyage | SubTaskSingleVoyage) => inPointContext
            ? item.executions?.find((entry) => entry.waypointId === Number(currentWaypointId))
            : undefined

        return (tasks ?? []).flatMap((task) => [
            {
                id: `t${task.id}`,
                small: false,
                state: taskStateSingleVoyage(executionHere(task)?.status ?? task.status),
            },
            ...(task.subtasks ?? [])
                .filter((subtask) => !inPointContext || !!executionHere(subtask))
                .map((subtask) => ({
                    id: `s${subtask.id}`,
                    small: true,
                    state: taskStateSingleVoyage(executionHere(subtask)?.status ?? subtask.status),
                })),
        ])
    }, [tasks, currentWaypointId])

    const doneCount = states.filter((item) => item.state === 'done').length

    const list = tasks?.length >= 1 && tasks.map((task, index) => {
        return (
            <Item
                key={index}
                task={task}
                showDescription={showDescription}
                hasChangeStatus={hasChangeStatuses}
                voyageId={voyageId}
                accountId={accountId}
                currentWaypointId={currentWaypointId}
                narrowToWaypoint={narrowToWaypoint}
                number={index + 1}
            />
        )
    })
    return (
        <Box
            sx={{
                ...sx.rootSmall,
                ...(sxProps?.card ?? {})
            }}
        >
            {!tasks?.length && <Stack
                sx={sx.nothing}
                alignItems={'center'}
                gap={1}
            >
                <Typography
                    sx={sx.nothingText}
                >
                    {t('singleVoyage.nothingTasksInPoint')}
                </Typography>

                <CancelIcon
                    sx={sx.clearIcon}
                />
            </Stack>}

            {tasks?.length >= 1 && <Stack
                direction={'row'}
                alignItems={'center'}
                sx={itemSx.tasksSummary}
                onClick={() => setOpen((prev) => !prev)}
                role={'button'}
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setOpen((prev) => !prev)
                    }
                }}
            >
                {states.map((item) => (
                    <TaskStateDotSingleVoyage
                        key={item.id}
                        small={item.small}
                        state={item.state}
                    />
                ))}

                <Typography sx={itemSx.tasksSummaryText}>
                    {t('singleVoyage.tasksStopSummary', {done: doneCount, total: states.length})}
                </Typography>

                <ExpandMoreIcon
                    sx={{
                        ...itemSx.tasksSummaryChevron,
                        transform: open ? 'rotate(180deg)' : 'none',
                    }}
                />
            </Stack>}

            {open && list}
        </Box>
    );
};

export default PointTasksInfoSmallViewSingleVoyage;
